import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Auth } from "aws-amplify";
import { UsersService } from "../users/users.service";

/** Route path of the screen a user without SalesMail access is sent to. */
export const NO_ACCESS_PATH = "no-access";

/** Matches AuthGuard's window for an SSO session to restore from storage. */
const SSO_RESTORE_WAIT_MS = 2000;

/**
 * Blocks the SalesMail product for accounts that do not have it enabled.
 *
 * Registered as canActivateChild on the MainComponent shell route, so every
 * child route is gated by default and any route added later is covered without
 * a second edit. It is also applied as canActivate to the /outlook and
 * /chrome-extension entry points and to the root wildcard, which sit outside
 * that shell.
 *
 * Returns a UrlTree rather than calling router.navigate() and returning false:
 * the router consumes the UrlTree as the redirect for the navigation it is
 * already processing, instead of racing a second navigation against the first.
 *
 * The redirect runs both ways: a blocked user is sent to /no-access from
 * anywhere else, and an entitled user is sent from /no-access to the dashboard.
 * The two cannot ping-pong, because each redirect targets a route that the same
 * entitlement answer resolves to true.
 *
 * FAILS CLOSED. allow_salesmail ships on every response from the deployed API,
 * so a missing flag or a failed lookup means something is wrong, not that the
 * account is entitled -- both deny access.
 */
@Injectable({
  providedIn: "root",
})
export class SalesMailAccessGuard implements CanActivate, CanActivateChild {

  constructor(private router: Router, private userService: UsersService) {}

  canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.check(next);
  }

  canActivateChild(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    return this.check(next);
  }

  private async check(next: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
    const isNoAccessRoute = next.routeConfig?.path === NO_ACCESS_PATH;

    // Entitlement is only a meaningful question for a signed-in user: with no
    // session there is nothing to look up. Defer rather than deny -- AuthGuard
    // covers the shell and the plugin routes, and the wildcard falls through to
    // NotFound.
    //
    // A cached user means a session was established, which is enough to skip the
    // probe. Without this the guard would call into Amplify once per activated
    // route node on every navigation -- three times for /users/detail/5 -- and
    // currentAuthenticatedUser() runs getSession(), which can refresh tokens. An
    // expired session is still caught: AuthGuard runs first on the shell, and at
    // index 0 on the plugin routes, so its answer wins.
    if (!this.userService.hasCachedUser && !(await this.hasActiveSession())) {
      return true;
    }

    const allowed = await this.userService.hasSalesMailAccess();

    // The no-access screen is checked in reverse rather than simply exempted.
    // Access can be switched on while someone is sitting on it, so a refresh
    // should drop them into the app instead of back onto the upsell screen.
    // (A reload is what makes this work: it empties the user cache, so the flag
    // is re-read rather than served from the value fetched at sign-in.)
    if (isNoAccessRoute) {
      return allowed ? this.router.parseUrl("/") : true;
    }

    if (allowed) {
      return true;
    }

    return this.router.parseUrl(`/${NO_ACCESS_PATH}`);
  }

  /**
   * Whether there is a usable Cognito session right now.
   *
   * Guards listed in a single canActivate array run in PARALLEL -- the router
   * subscribes to all of them at once and only prioritizes the results by array
   * position. So on /outlook and /chrome-extension this guard cannot assume
   * AuthGuard has already finished restoring an SSO session. Answering from a
   * half-restored state would read as "no session", defer, and wave a blocked
   * SSO user through for that navigation. Waiting out the same restore window
   * AuthGuard uses gets a real answer instead.
   */
  private async hasActiveSession(): Promise<boolean> {
    try {
      await Auth.currentAuthenticatedUser();
      return true;
    } catch {
      // Nothing stored means genuinely signed out -- no point waiting.
      if (!this.hasStoredSession()) {
        return false;
      }
    }

    await new Promise(resolve => setTimeout(resolve, SSO_RESTORE_WAIT_MS));

    try {
      await Auth.currentAuthenticatedUser();
      return true;
    } catch {
      return false;
    }
  }

  /** Cognito tokens in local storage mean a session is present but not yet restored. */
  private hasStoredSession(): boolean {
    try {
      return Object.keys(localStorage).some(key =>
        key.includes("CognitoIdentityServiceProvider") &&
        (key.includes(".idToken") || key.includes(".accessToken") || key.includes("LastAuthUser"))
      );
    } catch {
      return false;
    }
  }
}
